import {z} from 'zod';

import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {refineEmailBody} from '@dms/shared';

import {
  EmailSmsNotificationForm,
  FormDocument,
  NotificationCategory,
} from './EmailSmsNotificationForm';

const emailSchema = z.string().email();

// Trimmed to match the cast the previous `yupString` fields performed - the values are
// sent to the API verbatim.
const trimmedString = z.string().trim();

/**
 * The per-recipient fields are only required once the channel is switched on, and the
 * e-mail subject and message only for the custom category - the checkin and handover
 * e-mails are rendered from a template.
 *
 * Annotated against `EmailSmsNotificationForm` rather than inferred: eight components read
 * the named sub-types, and the annotation is what makes the compiler catch a field the
 * schema forgets to declare - `z.object` would silently strip it from the sent payload.
 */
export const EmailSmsNotificationFormSchema: z.ZodSchema<EmailSmsNotificationForm> = z
  .object({
    category: z.array(z.nativeEnum(NotificationCategory)),
    email: z.object({
      isEnabled: z.boolean(),
      data: z.object({
        person: trimmedString,
        email: z.array(trimmedString),
        subject: trimmedString.nullable(),
        message: trimmedString,
        documents: z.array(z.custom<FormDocument>()),
      }),
    }),
    sms: z.object({
      isEnabled: z.boolean(),
      data: z.object({
        person: trimmedString,
        phone: z.array(trimmedString),
        message: trimmedString,
      }),
    }),
  })
  .superRefine((values, ctx) => {
    const addIssue = (path: (string | number)[], message: string) =>
      ctx.addIssue({code: z.ZodIssueCode.custom, path, message});

    const requireValue = (path: (string | number)[], value: string | null) => {
      if (isNilOrEmpty(value)) {
        addIssue(path, i18n.t('general.validations.fieldIsRequired'));
      }
    };

    if (values.email.isEnabled) {
      const {person, email, subject, message} = values.email.data;

      requireValue(['email', 'data', 'person'], person);

      if (isNilOrEmpty(email)) {
        addIssue(['email', 'data', 'email'], i18n.t('general.validations.fieldIsRequired'));
      }

      email.forEach((address, index) => {
        if (!emailSchema.safeParse(address).success) {
          addIssue(['email', 'data', 'email', index], i18n.t('general.validations.invalidEmail'));
        }
      });

      if (values.category.includes(NotificationCategory.CUSTOM)) {
        requireValue(['email', 'data', 'subject'], subject);

        // Rich text: a cleared editor emits `<p><br></p>`, which `isNilOrEmpty` accepts. The SMS
        // message below stays plain text and keeps `requireValue`.
        const bodyResult = refineEmailBody(z.string()).safeParse(message);
        if (!bodyResult.success) {
          bodyResult.error.issues.forEach((issue) =>
            addIssue(['email', 'data', 'message'], issue.message)
          );
        }
      }
    }

    if (values.sms.isEnabled) {
      const {person, phone, message} = values.sms.data;

      requireValue(['sms', 'data', 'person'], person);

      if (isNilOrEmpty(phone)) {
        addIssue(['sms', 'data', 'phone'], i18n.t('general.validations.fieldIsRequired'));
      }

      requireValue(['sms', 'data', 'message'], message);
    }
  });
